import type { MarketingDraft, MarketingStep } from './types';
import { emailVariableTexts } from './emailBlocks';
import { pushVariableTexts } from './push';

export const MAX_PROMO_CODES = 20_000;
export const MAX_PROMO_FILE_BYTES = 5_242_880;
export const PROMO_EXAMPLE = 'ПРИМЕР-КОДА';

export function isPromoHeader(value: string): boolean {
  return /^(промокоды?|promocodes?)$/.test(
    value
      .trim()
      .replace(/^"|"$/g, '')
      .replace(/[\s_-]+/g, '')
      .toLowerCase(),
  );
}

/** Один столбец кодов; известное название столбца не является промокодом. */
export function parsePromoCodes(text: string) {
  const codes: string[] = [];
  const invalidLines: number[] = [];
  const seen = new Set<string>();
  let duplicates = 0;
  let rows = 0;
  let headerSkipped = false;
  let first = true;
  for (const [index, original] of text
    .replace(/^\uFEFF/, '')
    .split(/\r\n|\n|\r/)
    .entries()) {
    let code = original.trim();
    if (!code) continue;
    if (first && isPromoHeader(code)) {
      headerSkipped = true;
      first = false;
      continue;
    }
    first = false;
    rows++;
    if (rows > MAX_PROMO_CODES) continue;
    if (/^"(?:[^"]|"")*"$/.test(code)) {
      code = code.slice(1, -1).replaceAll('""', '"').trim();
    }
    if (!/^[\p{L}\p{N}._-]+$/u.test(code) || Array.from(code).length > 100) {
      invalidLines.push(index + 1);
      continue;
    }
    const key = Array.from(code)
      .map((letter) => {
        const upper = letter.toUpperCase();
        return Array.from(upper).length === 1 ? upper : letter;
      })
      .join('');
    if (seen.has(key)) duplicates++;
    else {
      seen.add(key);
      codes.push(code);
    }
  }
  return {
    codes,
    rows,
    duplicates,
    invalidLines,
    tooMany: rows > MAX_PROMO_CODES,
    headerSkipped,
  };
}

export function normalizeStepPromo(
  step: MarketingStep,
  legacy = '',
): MarketingStep {
  if (step.promoMode) return { ...step };
  return {
    ...step,
    promoMode: legacy.trim() ? 'shared' : 'none',
    promoCode: legacy,
    promoPoolId: '',
  };
}

export function previewPromo(step: MarketingStep, legacy = ''): string {
  const normalized = normalizeStepPromo(step, legacy);
  return normalized.promoMode === 'pool'
    ? PROMO_EXAMPLE
    : normalized.promoMode === 'shared'
      ? (normalized.promoCode ?? '')
      : '';
}

export function stepHasPromoVariable(step: MarketingStep): boolean {
  const text =
    step.channel === 'sms'
      ? step.smsText
      : step.channel === 'push' ? pushVariableTexts(step.push).join('\n') : emailVariableTexts(step.email).join('\n');
  return /\{(?:promo|промокод)\}/.test(text);
}

export function campaignCityIds(
  draft: Pick<MarketingDraft, 'tenantId' | 'tenantIds'>,
): string[] {
  return draft.tenantIds?.length
    ? draft.tenantIds
    : draft.tenantId
      ? [draft.tenantId]
      : [];
}

export function sendTimeExample(offsetMinutes: number): string {
  if (
    !Number.isInteger(offsetMinutes) ||
    offsetMinutes < 0 ||
    offsetMinutes > 1440
  )
    return '—';
  const minutes = (((19 * 60 + 40 - offsetMinutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}
